import type { Me } from "../services/api";

import { features, type FeatureKey } from "./features";

/**
 * The single source of truth for the left-sidebar navigation tree.
 *
 * Sections that used to open a second left rail (Service Hub, Knowledge Base,
 * Admin) now expand their sub-sections inline beneath the parent in the main
 * sidebar — one menu, not a menu-then-another-menu (issue #7). The layout
 * components (ServiceHubLayout/KbLayout/AdminLayout) are now thin <Outlet/>
 * wrappers; this file owns the nav data they used to carry.
 */

export type NavLeaf = {
  to: string;
  label: string;
  /** Exact-match active highlighting (the section index, e.g. "/" or "/services"). */
  end?: boolean;
  /** Shows the pending-approvals badge when the count is > 0. */
  trackPending?: boolean;
};

export type NavEntry = NavLeaf & {
  /** When present, an expandable group whose children render indented below. */
  children?: NavLeaf[];
};

export type NavGroup = {
  heading: string;
  items: NavEntry[];
};

/**
 * Admin sub-sections, each gated by role. Reproduces the per-link visibility
 * that previously lived in AdminLayout, so who-sees-what is unchanged — only
 * where the nav lives.
 */
const ADMIN_SECTIONS: (NavLeaf & {
  roles: readonly string[];
  /** When set, the section is hidden unless this build-time feature flag is on. */
  feature?: FeatureKey;
})[] = [
  // Landing page for the admin area: search, quick actions and the
  // needs-attention feed. `end` so it only highlights on /admin itself.
  {
    to: "/admin",
    label: "Overview",
    end: true,
    roles: ["iquest_admin", "service_desk"],
  },
  { to: "/admin/clients", label: "Customers", roles: ["iquest_admin"] },
  {
    to: "/admin/announcements",
    label: "Status alerts",
    roles: ["iquest_admin", "service_desk"],
  },
  { to: "/admin/forms", label: "Forms", roles: ["iquest_admin", "service_desk"] },
  {
    to: "/admin/kb",
    label: "Knowledge base",
    roles: ["iquest_admin", "service_desk"],
    feature: "kb",
  },
  // iQuest-admin only: this page lists and creates iQuest staff accounts, which
  // see every tenant. A Client Admin manages their own people from Clients.
  { to: "/admin/users", label: "iQuest staff", roles: ["iquest_admin"] },
  {
    to: "/admin/view-as",
    label: "View as user",
    roles: ["iquest_admin", "service_desk"],
  },
];

/** The admin sub-sections a given role may see. */
export function allowedAdminSections(role: string | undefined): NavLeaf[] {
  if (!role) return [];
  return ADMIN_SECTIONS.filter(
    (s) => s.roles.includes(role) && (!s.feature || features[s.feature]),
  ).map(({ roles: _roles, feature: _feature, ...leaf }) => leaf);
}

/**
 * The Service Hub lifecycle: browse and submit (Forms Catalogue), track your
 * submissions (My Requests), act on what's waiting on you (Approvals), and hand
 * your approval authority over while you're out (Delegation).
 */
const HUB_SECTIONS: NavLeaf[] = [
  { to: "/services", label: "Forms Catalogue", end: true },
  { to: "/services/requests", label: "My Requests" },
  { to: "/services/approvals", label: "Approvals", trackPending: true },
  { to: "/services/delegation", label: "Delegation" },
];

const KB_SECTIONS: NavLeaf[] = [
  { to: "/kb", label: "Browse", end: true },
  { to: "/kb/categories", label: "Categories" },
  { to: "/kb/saved", label: "Saved" },
  { to: "/kb/recent", label: "Recently Viewed" },
];

/**
 * Builds the sidebar nav tree for the current user. Applies the same build-time
 * feature flags (SLA/KB/Trust) and server-side gates (delegation kill-switch,
 * admin roles) that the nav and routes already enforced.
 */
export function buildNav(me: Me | undefined): NavGroup[] {
  // Delegation is hidden when the server-side kill-switch is off (absence = on);
  // the /services/delegation route stays mounted so a saved link is still honest.
  const delegationOn = me?.approval_delegation_enabled !== false;
  const hubChildren = HUB_SECTIONS.filter(
    (s) => s.to !== "/services/delegation" || delegationOn,
  );
  const isStaff = me?.role === "iquest_admin" || me?.role === "service_desk";

  const workspace: (NavEntry | null)[] = [
    { to: "/", label: "Dashboard", end: true },
    { to: "/tickets", label: "Tickets" },
    // Named for the contract it reports against: "Service Level Agreement" is
    // the term clients hold us to and expect to find (issue #105).
    features.sla ? { to: "/sla", label: "Service Level Agreement" } : null,
    // Reporting: build-flag off in prod, and only shown to users the server says
    // may view reports (staff / client_admin / view_reports grant), so a plain
    // client never sees an empty Reports tab even on staging.
    features.reporting && me?.may_view_reports
      ? { to: "/reports", label: "Reports" }
      : null,
    { to: "/services", label: "Service Hub", trackPending: true, children: hubChildren },
    features.kb ? { to: "/kb", label: "Knowledge base", children: KB_SECTIONS } : null,
    // Feedback no longer lives in the nav: the top-right ⓘ button (AppShell)
    // opens the Send-feedback form, and the full ticket history lives in
    // Settings → Help & Support (/me/preferences).
    features.trust ? { to: "/trust", label: "Trust & Security" } : null,
    // iQuest staff get Admin as one expandable group at the foot of the menu;
    // its sub-sections are role-gated. Keeping it in the single Workspace list
    // (rather than under its own "Admin" heading) avoids the duplicate "Admin /
    // Admin" the separate heading produced (issue #10).
    isStaff
      ? { to: "/admin", label: "Admin", children: allowedAdminSections(me?.role) }
      : null,
  ];

  const groups: NavGroup[] = [
    { heading: "Workspace", items: workspace.filter((i): i is NavEntry => i !== null) },
  ];

  return groups;
}

/** Whether a path falls within a section group (parent route or any descendant). */
export function isWithinSection(pathname: string, sectionTo: string): boolean {
  return pathname === sectionTo || pathname.startsWith(sectionTo + "/");
}
